// Room numbers editor — add / rename / delete physical units of a room type.
import React, { useEffect, useState } from 'react';
import { AlertTriangle, Loader2, Plus, RefreshCw, Save, Trash2, X } from 'lucide-react';
import { apiCall } from './_shared';

const RoomUnitsEditor = ({ room, onClose, onSaved }) => {
  const [units, setUnits] = useState([]);
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = async () => {
    try {
      const d = await apiCall(`/api/admin/rooms/${room.id}/units`);
      setMeta(d); setUnits(d.units.map((u) => ({ ...u })));
    } catch (e) { setErr(e.message); }
  };
  useEffect(() => { load(); }, [room.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setBusy(true); setErr('');
    try {
      const d = await apiCall(`/api/admin/rooms/${room.id}/units`, { method: 'PUT', body: JSON.stringify({ units: units.map((u) => ({ unit_id: u.unit_id ?? null, label: u.label })) }) });
      setMeta(d); setUnits(d.units.map((u) => ({ ...u })));
      onSaved?.(d);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const resync = async () => {
    if (!window.confirm('Replace these room numbers with the ones configured in Beds24?')) return;
    setBusy(true); setErr('');
    try {
      const d = await apiCall(`/api/admin/rooms/${room.id}/units/resync`, { method: 'POST' });
      setMeta(d); setUnits(d.units.map((u) => ({ ...u })));
      onSaved?.(d);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const rows = units.map((u, i) => (
    <li key={u.unit_id ?? `new-${i}`} className="flex items-center gap-2" data-testid={`unit-row-${u.unit_id ?? 'new-' + i}`}>
      <span className="text-[11px] text-slate-400 w-10">#{u.unit_id ?? 'new'}</span>
      <input
        value={u.label}
        onChange={(e) => setUnits(units.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
        className="flex-1 border rounded-lg px-3 py-1.5 text-sm font-semibold"
        placeholder="Room number, e.g. 716"
        data-testid={`unit-label-${u.unit_id ?? 'new-' + i}`}
      />
      <button onClick={() => setUnits(units.filter((_, j) => j !== i))} className="p-1.5 rounded hover:bg-red-50" title="Delete room number" data-testid={`unit-delete-${u.unit_id ?? 'new-' + i}`}>
        <Trash2 className="w-4 h-4 text-red-400" />
      </button>
    </li>
  ));

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="room-units-editor">
        <div className="p-4 bg-slate-900 text-white flex justify-between items-start rounded-t-xl">
          <div>
            <h3 className="font-bold">Room numbers · {room.name}</h3>
            <p className="text-xs text-slate-300 mt-0.5">
              {units.length} physical room{units.length === 1 ? '' : 's'}
              {meta?.locked ? ' · custom (Beds24 auto-sync paused)' : ' · synced from Beds24'}
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/20" data-testid="room-units-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto">
          <p className="text-xs text-slate-500 flex gap-2 bg-amber-50 border border-amber-200 rounded-lg p-2">
            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
            Changes apply instantly to the Calendar, Housekeeping board, QR sheet and handoff. Keep numbers aligned with your Beds24 unit setup so channel bookings land on the right room.
          </p>
          <ul className="space-y-1.5" data-testid="unit-list">{rows}</ul>
          <button onClick={() => setUnits([...units, { unit_id: null, label: '' }])} className="text-sm font-semibold text-orange-600 hover:underline flex items-center gap-1" data-testid="unit-add">
            <Plus className="w-4 h-4" /> Add room number
          </button>
          {err && <p className="text-sm text-red-600" data-testid="room-units-error">{err}</p>}
        </div>
        <div className="p-4 border-t flex items-center gap-2">
          <button onClick={save} disabled={busy} className="px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold flex items-center gap-1 disabled:opacity-50" data-testid="room-units-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
          </button>
          <button onClick={resync} disabled={busy} className="px-3 py-2 rounded-lg border text-sm font-semibold flex items-center gap-1 hover:bg-slate-50 disabled:opacity-50" title="Pull room numbers from Beds24" data-testid="room-units-resync">
            <RefreshCw className="w-4 h-4" /> Re-sync from Beds24
          </button>
          <button onClick={onClose} className="ml-auto px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Close</button>
        </div>
      </div>
    </div>
  );
};

export default RoomUnitsEditor;
